import { useState } from 'react'
import { Stepper, Button, Input, Select, Chip, Alert, ChatBubble, ChatContainer, PriorityChip } from '@/components/ui'
import { templates } from '@/mock'
import type { CampanaTipo, Prioridad } from '@/mock'
import { AlertTriangle, CalendarCheck, Rocket, ArrowLeft, ArrowRight, Save, PlayCircle, RefreshCw, Phone, Infinity as InfIcon, Calendar, Zap, Plus, Trash2 } from 'lucide-react'
import { cn, fmtMoney } from '@/lib/utils'

type Vigencia = 'rango' | 'mes' | 'siempre'
type EstadoReenvio = 'entregado' | 'leido' | 'cualquiera'

interface Tanda {
  id: string
  hora: string
  max: number
}

const STEPS = ['Tipo', 'Filtros', 'Tandas', 'Template', 'Simular', 'Confirmar']

export function CampanasCrear() {
  const [step, setStep] = useState(0)
  // Step 1
  const [tipo, setTipo] = useState<CampanaTipo>('mora')
  const [prioridad, setPrioridad] = useState<Prioridad>('P2')
  const [nombre, setNombre] = useState('Mora 15 días Guayaquil')
  const [descripcion, setDescripcion] = useState('Recordatorio a clientes con 15 días vencidos')
  const [vigencia, setVigencia] = useState<Vigencia>('siempre')
  const [vigDesde, setVigDesde] = useState('2026-10-05')
  const [vigHasta, setVigHasta] = useState('2026-10-10')
  const [reenvioBase, setReenvioBase] = useState('c-prev-nacional')
  const [reenvioDias, setReenvioDias] = useState(3)
  const [reenvioEstado, setReenvioEstado] = useState<EstadoReenvio>('leido')
  // Step 2
  const [moraDesde, setMoraDesde] = useState('3')
  const [moraHasta, setMoraHasta] = useState('9')
  const [plaza, setPlaza] = useState('GUAYAQUIL')
  const [saldoMin, setSaldoMin] = useState('25')
  const [saldoMax, setSaldoMax] = useState('500')
  const [cuotasMin, setCuotasMin] = useState('1')
  const [intentosMax, setIntentosMax] = useState('5')
  const [excluirHumana, setExcluirHumana] = useState(true)
  const [prevDias, setPrevDias] = useState<number[]>([1, 2, 3, 4, 5])
  const [montoCuota, setMontoCuota] = useState('10')
  const [clienteRec, setClienteRec] = useState<'todos' | 'recurrente' | 'nuevo'>('todos')
  // Step 3
  const [dias, setDias] = useState<number[]>([1, 2, 3, 4, 5])
  const [tandas, setTandas] = useState<Tanda[]>([
    { id: 't1', hora: '10:00', max: 100 },
    { id: 't2', hora: '15:00', max: 100 },
  ])
  // Step 4
  const [templateName, setTemplateName] = useState('mora_recordatorio_v1')
  // Step 5 simulación
  const [simulado, setSimulado] = useState(false)

  const tpl = templates.find(t => t.name === templateName)!

  const canNext = () => {
    if (step === 0) return nombre.trim().length > 0
    if (step === 2) return tandas.length > 0 && dias.length > 0
    return true
  }

  const togglePrev = (d: number) =>
    setPrevDias(p => p.includes(d) ? p.filter(x => x !== d) : [...p, d].sort())

  const toggleDia = (d: number) =>
    setDias(p => p.includes(d) ? p.filter(x => x !== d) : [...p, d].sort())

  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-xl px-5 py-4">
        <Stepper
          steps={STEPS}
          current={step}
          onJump={i => setStep(i)}
        />
      </div>

      {/* STEP 1: Tipo + datos */}
      {step === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-6">
          <h2 className="font-display text-xl text-navy-700">Tipo y datos básicos</h2>

          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 block">Tipo de campaña</label>
            <div className="grid grid-cols-3 gap-3">
              {([
                { k: 'preventiva' as const, label: 'Preventiva', sub: 'Antes del vencimiento', icon: CalendarCheck, color: 'blue' },
                { k: 'mora' as const, label: 'Mora', sub: 'Cuotas vencidas impagas', icon: AlertTriangle, color: 'rose' },
                { k: 'reenvio' as const, label: 'Reenvío', sub: 'Insistir a los que no respondieron', icon: Rocket, color: 'violet', advanced: true },
              ]).map(opt => {
                const active = tipo === opt.k
                const Icon = opt.icon
                return (
                  <button
                    key={opt.k}
                    onClick={() => setTipo(opt.k)}
                    className={cn(
                      'relative p-4 rounded-xl border-2 text-left transition-all',
                      active
                        ? opt.color === 'blue' ? 'border-blue-500 bg-blue-50'
                          : opt.color === 'rose' ? 'border-rose-500 bg-rose-50'
                          : 'border-violet-500 bg-violet-50'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    )}
                  >
                    {opt.advanced && (
                      <span className="absolute top-2 right-2 text-[9px] uppercase font-bold text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded">Avanzado</span>
                    )}
                    <Icon className={cn('w-5 h-5 mb-2',
                      opt.color === 'blue' ? 'text-blue-600'
                        : opt.color === 'rose' ? 'text-rose-600'
                        : 'text-violet-600')} />
                    <div className="font-semibold text-navy-700">{opt.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{opt.sub}</div>
                  </button>
                )
              })}
            </div>
          </div>

          {tipo === 'reenvio' && (
            <div className="bg-violet-50/50 border border-violet-200 rounded-xl p-4 space-y-3">
              <div className="text-xs font-semibold text-violet-700 uppercase tracking-wider">Datos específicos de reenvío</div>
              <div className="grid grid-cols-3 gap-3">
                <Select label="Campaña base" value={reenvioBase} onChange={e => setReenvioBase(e.target.value)}>
                  <option value="c-mora-15d-gye">Mora 15d Guayaquil</option>
                  <option value="c-prev-nacional">Preventiva nacional</option>
                </Select>
                <Input label="Días sin responder" type="number" value={reenvioDias} onChange={e => setReenvioDias(Number(e.target.value))} />
                <Select label="Estado del envío original" value={reenvioEstado} onChange={e => setReenvioEstado(e.target.value as EstadoReenvio)}>
                  <option value="entregado">Entregado pero no leído</option>
                  <option value="leido">Leído sin responder</option>
                  <option value="cualquiera">Cualquiera</option>
                </Select>
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 block">Prioridad</label>
            <div className="grid grid-cols-5 gap-2">
              {([
                { k: 'P1' as const, l: 'Crítica', s: 'Mora 30+ días' },
                { k: 'P2' as const, l: 'Alta', s: 'Mora 15-30d' },
                { k: 'P3' as const, l: 'Media', s: 'Mora 5-14d' },
                { k: 'P4' as const, l: 'Baja', s: 'Preventiva' },
                { k: 'P5' as const, l: 'Info', s: 'Avisos' },
              ]).map(p => (
                <button
                  key={p.k}
                  onClick={() => setPrioridad(p.k)}
                  className={cn(
                    'p-3 rounded-lg border-2 text-left transition',
                    prioridad === p.k
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  )}
                >
                  <PriorityChip prioridad={p.k} />
                  <div className="font-semibold text-navy-700 text-sm mt-1">{p.l}</div>
                  <div className="text-[10px] text-slate-500">{p.s}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input label="Nombre" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Mora 15 días Guayaquil" />
            <Input label="Descripción" value={descripcion} onChange={e => setDescripcion(e.target.value)} placeholder="Recordatorio a clientes con…" />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 block">Vigencia</label>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {([
                { k: 'rango' as const, l: 'Rango de fechas', icon: Calendar },
                { k: 'mes' as const, l: 'Todo el mes', icon: CalendarCheck },
                { k: 'siempre' as const, l: 'Siempre ∞', icon: Zap },
              ]).map(opt => {
                const active = vigencia === opt.k
                const Icon = opt.icon
                return (
                  <button
                    key={opt.k}
                    onClick={() => setVigencia(opt.k)}
                    className={cn(
                      'p-3 rounded-lg border-2 flex items-center gap-2 transition text-sm',
                      active ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    {opt.l}
                  </button>
                )
              })}
            </div>
            {vigencia === 'rango' && (
              <div className="grid grid-cols-2 gap-3">
                <Input label="Desde" type="date" value={vigDesde} onChange={e => setVigDesde(e.target.value)} />
                <Input label="Hasta" type="date" value={vigHasta} onChange={e => setVigHasta(e.target.value)} />
              </div>
            )}
            {vigencia === 'mes' && (
              <div className="bg-slate-50 rounded-lg px-4 py-3 text-sm text-slate-600">
                Se ejecuta todo el mes de octubre 2026.
              </div>
            )}
            {vigencia === 'siempre' && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-700 flex items-center gap-2">
                <InfIcon className="w-4 h-4" /> Regla permanente — se ejecuta en todas las tandas mientras siga activa.
              </div>
            )}
          </div>
        </div>
      )}

      {/* STEP 2: Filtros */}
      {step === 1 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl text-navy-700">Filtros de segmentación</h2>
            <Chip variant={tipo === 'mora' ? 'rose' : tipo === 'preventiva' ? 'blue' : 'violet'}>
              Variables activas: {tipo === 'mora' ? 'Mora' : tipo === 'preventiva' ? 'Preventiva' : 'Reenvío'}
            </Chip>
          </div>

          {tipo === 'reenvio' && (
            <div className="bg-violet-50/50 border border-violet-200 rounded-xl p-4 space-y-3">
              <div className="text-xs font-semibold text-violet-700 uppercase tracking-wider">Filtros específicos de reenvío</div>
              <div className="grid grid-cols-2 gap-3">
                <Select label="Riesgo del crédito"><option>Cualquiera</option><option>Alto</option><option>Medio</option><option>Bajo</option></Select>
                <Select label="Historial de pago"><option>Irregular (atrasos frecuentes)</option><option>Regular</option><option>Cualquiera</option></Select>
                <Select label="Compromisos previos"><option>Incumplió al menos 1</option><option>Cumplió todos</option><option>Sin historial</option><option>Cualquiera</option></Select>
                <Select label="Ya recibió reenvío"><option>Es el primer reenvío</option><option>Ya tuvo 1 reenvío</option><option>Cualquiera</option></Select>
              </div>
            </div>
          )}

          {(tipo === 'mora' || tipo === 'reenvio') && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Días de mora</label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={moraDesde} onChange={e => setMoraDesde(e.target.value)} />
                  <span className="text-slate-400">a</span>
                  <Input type="number" value={moraHasta} onChange={e => setMoraHasta(e.target.value)} />
                </div>
              </div>
              <Select label="Plaza" value={plaza} onChange={e => setPlaza(e.target.value)}>
                <option>GUAYAQUIL</option><option>QUITO</option><option>MANTA</option><option>CUENCA</option><option>LOJA</option><option value="">Todas</option>
              </Select>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Saldo vencido (USD)</label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={saldoMin} onChange={e => setSaldoMin(e.target.value)} />
                  <span className="text-slate-400">a</span>
                  <Input type="number" value={saldoMax} onChange={e => setSaldoMax(e.target.value)} />
                </div>
              </div>
              <Input label="Cuotas vencidas min" type="number" value={cuotasMin} onChange={e => setCuotasMin(e.target.value)} />
              <Input label="Intentos previos máx" type="number" value={intentosMax} onChange={e => setIntentosMax(e.target.value)} hint="(opcional)" />
              <label className="flex items-start gap-2 pt-6 cursor-pointer">
                <input type="checkbox" checked={excluirHumana} onChange={e => setExcluirHumana(e.target.checked)} className="mt-0.5 w-4 h-4 rounded text-amber-500 focus:ring-amber-500" />
                <span className="text-sm text-slate-700">Excluir si ya está en <span className="font-semibold">gestión humana</span></span>
              </label>
            </div>
          )}

          {tipo === 'preventiva' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Días previos al vencimiento</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5, 6, 7].map(d => (
                    <button
                      key={d}
                      onClick={() => togglePrev(d)}
                      className={cn(
                        'flex-1 py-2 rounded-md text-sm font-semibold transition',
                        prevDias.includes(d) ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      )}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <Select label="Plaza"><option>Todas</option><option>GUAYAQUIL</option><option>QUITO</option></Select>
              <Input label="Monto cuota mínimo (USD)" type="number" value={montoCuota} onChange={e => setMontoCuota(e.target.value)} />
              <Select label="Cliente" value={clienteRec} onChange={e => setClienteRec(e.target.value as typeof clienteRec)}>
                <option value="todos">Todos</option>
                <option value="recurrente">Solo recurrentes</option>
                <option value="nuevo">Solo nuevos</option>
              </Select>
            </div>
          )}

          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3">Alcance estimado</div>
            <div className="grid grid-cols-5 gap-2">
              {[
                { l: 'Hoy', v: 63 },
                { l: 'Mañana', v: 58 },
                { l: '+2 días', v: 71 },
                { l: '+3 días', v: 82 },
                { l: 'Total período', v: 274, amber: true },
              ].map((s, i) => (
                <div key={i} className={cn('bg-white rounded-lg p-3 text-center', s.amber && 'ring-2 ring-amber-300')}>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{s.l}</div>
                  <div className={cn('font-mono text-xl font-semibold mt-1', s.amber ? 'text-amber-600' : 'text-navy-700')}>
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* STEP 3: Tandas */}
      {step === 2 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-6">
          <h2 className="font-display text-xl text-navy-700">Tandas y horarios</h2>
          <p className="text-sm text-slate-500">Cuántas tandas por día, a qué hora, en qué días de la semana.</p>

          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 block">Días de la semana</label>
            <div className="flex gap-2">
              {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((d, i) => {
                const day = i + 1
                const active = dias.includes(day)
                return (
                  <button
                    key={d}
                    onClick={() => toggleDia(day)}
                    className={cn(
                      'flex-1 py-2.5 rounded-lg text-sm font-semibold transition',
                      active ? 'bg-amber-500 text-white shadow-md shadow-amber-500/20' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    )}
                  >
                    {d}
                  </button>
                )
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 block">Tandas por día</label>
            <div className="space-y-2">
              {tandas.map((t, i) => (
                <div key={t.id} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                  <div className="w-16 h-9 flex items-center justify-center bg-navy-700 text-white rounded-md text-xs font-semibold">
                    Tanda {i + 1}
                  </div>
                  <input
                    type="time"
                    value={t.hora}
                    onChange={e => setTandas(prev => prev.map(x => x.id === t.id ? { ...x, hora: e.target.value } : x))}
                    className="px-3 py-2 border border-slate-300 rounded-md text-sm font-mono bg-white focus:border-amber-500 focus:outline-none"
                  />
                  <div className="flex items-center gap-2 text-sm text-slate-600 flex-1">
                    <span>máx</span>
                    <input
                      type="number"
                      value={t.max}
                      onChange={e => setTandas(prev => prev.map(x => x.id === t.id ? { ...x, max: Number(e.target.value) } : x))}
                      className="w-24 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono bg-white focus:border-amber-500 focus:outline-none"
                    />
                    <span>mensajes</span>
                  </div>
                  <button
                    onClick={() => setTandas(prev => prev.filter(x => x.id !== t.id))}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-rose-500 hover:bg-rose-50 transition"
                    disabled={tandas.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setTandas(prev => [...prev, { id: `t${Date.now()}`, hora: '12:00', max: 100 }])}
                className="w-full py-2.5 border-2 border-dashed border-slate-300 rounded-lg text-sm text-slate-500 hover:border-amber-400 hover:text-amber-600 transition flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" /> Agregar tanda
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 4: Template */}
      {step === 3 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-6">
          <h2 className="font-display text-xl text-navy-700">Template Meta aprobado</h2>
          <p className="text-sm text-slate-500">Solo <span className="font-mono bg-slate-100 px-1 rounded">UTILITY</span> aprobados — más baratos y no requieren opt-in.</p>

          <div className="grid grid-cols-3 gap-3">
            {templates.map(t => {
              const active = templateName === t.name
              return (
                <button
                  key={t.name}
                  onClick={() => setTemplateName(t.name)}
                  className={cn(
                    'relative text-left p-4 rounded-xl border-2 transition',
                    active ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-white hover:border-slate-300'
                  )}
                >
                  {active && (
                    <span className="absolute top-2 right-2 text-[9px] uppercase font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Seleccionado</span>
                  )}
                  <Chip variant="emerald" className="text-[10px]">✓ {t.category} · {t.status}</Chip>
                  <div className="font-mono text-sm font-semibold text-navy-700 mt-2">{t.name}</div>
                  <div className="text-[11px] text-slate-500 mt-1">{t.variables} variables</div>
                  <div className="bg-slate-50 rounded-md p-2 mt-2 text-[11px] text-slate-600 font-mono leading-relaxed line-clamp-3">
                    {t.body}
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono mt-2">{fmtMoney(t.costo)}/msg</div>
                </button>
              )
            })}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Mensaje final</div>
              <ChatContainer className="rounded-lg h-56">
                <ChatBubble from="out" time="14:30 ✓✓">
                  {tpl.body.replace('{{1}}', '59.74').replace('{{2}}', '1869275').replace('{{3}}', '15')}
                </ChatBubble>
              </ChatContainer>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Cliente del segmento (ejemplo)</div>
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div>
                  <div className="font-semibold text-navy-700">MIELES CEDEÑO LIGIA ELENA</div>
                  <div className="text-xs text-slate-500">+593 99 XXX 2466 · Guayaquil · Refrigeradora</div>
                </div>
                <div className="space-y-1 text-xs font-mono">
                  <div><span className="text-amber-600 font-semibold">{'{{1}}'}</span> saldo vencido → <span className="text-navy-700 font-semibold">$59.74</span></div>
                  <div><span className="text-amber-600 font-semibold">{'{{2}}'}</span> crédito → <span className="text-navy-700 font-semibold">#1869275</span></div>
                  <div><span className="text-amber-600 font-semibold">{'{{3}}'}</span> días vencidos → <span className="text-navy-700 font-semibold">15</span></div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm"><RefreshCw className="w-3.5 h-3.5" /> Otro cliente</Button>
                  <Button variant="outline" size="sm"><Phone className="w-3.5 h-3.5" /> Enviármelo a prueba</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* STEP 5: Simular */}
      {step === 4 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
          <h2 className="font-display text-xl text-navy-700">Simular (dry-run)</h2>

          <div className="bg-slate-50 rounded-xl p-4 grid grid-cols-3 gap-x-6 gap-y-3 text-sm">
            <Resumen k="Tipo" v={<Chip variant={tipo === 'mora' ? 'rose' : tipo === 'preventiva' ? 'blue' : 'violet'}>● {tipo}</Chip>} />
            <Resumen k="Nombre" v={nombre} />
            <Resumen k="Vigencia" v={vigencia === 'siempre' ? 'Siempre ∞' : vigencia === 'mes' ? 'Todo oct 2026' : `${vigDesde} → ${vigHasta}`} />
            <Resumen k="Prioridad" v={<PriorityChip prioridad={prioridad} withLabel />} />
            <Resumen k="Tandas" v={`${tandas.length} (${tandas.map(t => t.hora).join(' · ')})`} />
            <Resumen k="Días" v={dias.length === 5 ? 'Lun-Vie' : dias.map(d => ['L', 'M', 'X', 'J', 'V', 'S', 'D'][d - 1]).join('·')} />
            <Resumen k="Template" v={templateName} />
            <Resumen k="Filtros" v={`${moraDesde}-${moraHasta}d · ${plaza} · $${saldoMin}-${saldoMax}`} />
          </div>

          {!simulado ? (
            <div className="text-center py-6">
              <Button variant="amber" size="lg" onClick={() => setSimulado(true)}>
                <PlayCircle className="w-5 h-5" /> Correr simulación
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <StatCard label="Coinciden filtros" value="63" sub="personas" color="navy" />
                <StatCard label="Serán contactadas hoy" value="55" sub="válidas" color="emerald" />
                <StatCard label="Excluidas" value="8" sub="ver detalle" color="amber" />
                <StatCard label="Costo estimado" value="$0.69" sub="USD" color="blue" />
              </div>

              <Alert variant="warning" title="8 personas serán excluidas">
                <ul className="text-xs space-y-0.5 mt-1">
                  <li>· 3 sin opt-in LOPDP vigente</li>
                  <li>· 2 dedup 48h (otra campaña / misma campaña)</li>
                  <li>· 2 en gestión humana activa</li>
                  <li>· 1 número WhatsApp inválido</li>
                </ul>
              </Alert>

              <div className="rounded-xl overflow-hidden border border-amber-200 bg-amber-50/30">
                <div className="px-4 py-2 bg-amber-100/50 text-amber-800 text-xs font-semibold uppercase tracking-wider">
                  Detalle excluidos (8)
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-amber-100/50 text-amber-800">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Cliente</th>
                      <th className="text-left px-3 py-2 font-semibold">Cédula</th>
                      <th className="text-left px-3 py-2 font-semibold">WhatsApp</th>
                      <th className="text-left px-3 py-2 font-semibold">Saldo</th>
                      <th className="text-left px-3 py-2 font-semibold">Motivo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-amber-100">
                    {excluidosMock.map((e, i) => (
                      <tr key={i} className="hover:bg-amber-50">
                        <td className="px-3 py-2 font-medium text-navy-700">{e.cliente}</td>
                        <td className="px-3 py-2 font-mono text-slate-600">{e.cedula}</td>
                        <td className={cn('px-3 py-2 font-mono text-slate-600', e.motivo.startsWith('📵') && 'line-through')}>{e.whatsapp}</td>
                        <td className="px-3 py-2 font-mono text-slate-700">${e.saldo.toFixed(2)}</td>
                        <td className="px-3 py-2 text-slate-700">{e.motivo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* STEP 6: Confirmar */}
      {step === 5 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
          <h2 className="font-display text-xl text-navy-700">Revisá los datos de tu campaña</h2>

          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-5 grid grid-cols-2 gap-x-8 gap-y-4">
            <Resumen k="Nombre" v={<span className="text-base font-semibold">{nombre}</span>} />
            <Resumen k="Tipo" v={<Chip variant={tipo === 'mora' ? 'rose' : tipo === 'preventiva' ? 'blue' : 'violet'}>● {tipo}</Chip>} />
            <Resumen k="Vigencia" v={vigencia === 'siempre' ? <span className="flex items-center gap-1">Siempre <InfIcon className="w-4 h-4" /></span> : `${vigDesde} → ${vigHasta}`} />
            <Resumen k="Días" v={dias.length === 5 ? 'Lun-Vie' : 'Personalizado'} />
            <Resumen k="Tandas/día" v={`${tandas.length} (${tandas.map(t => t.hora).join(' · ')})`} />
            <Resumen k="Alcance hoy" v={<span className="text-emerald-600 font-semibold">63 personas</span>} />
            <Resumen k="Template" v={<span className="font-mono">{templateName}</span>} />
            <Resumen k="Costo estimado hoy" v={<span className="text-amber-600 font-semibold">~$0.79 USD</span>} />
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-700">
            Todo listo. Al crear la campaña, la primera tanda se programará automáticamente.
          </div>
        </div>
      )}

      {/* NAV */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}>
          <ArrowLeft className="w-4 h-4" /> Volver
        </Button>
        <div className="text-xs text-slate-500">
          Paso <span className="font-semibold text-navy-700">{step + 1}</span> de {STEPS.length}
        </div>
        {step < STEPS.length - 1 ? (
          <Button variant="amber" onClick={() => setStep(s => Math.min(STEPS.length - 1, s + 1))} disabled={!canNext()}>
            Siguiente · {STEPS[step + 1]} <ArrowRight className="w-4 h-4" />
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline"><Save className="w-4 h-4" /> Guardar borrador</Button>
            <Button variant="emerald"><Rocket className="w-4 h-4" /> Crear y activar</Button>
          </div>
        )}
      </div>
    </div>
  )
}

const excluidosMock = [
  { cliente: 'ZAMBRANO MERA JULIO', cedula: '0908223344', whatsapp: '+593 99 XXX 1122', saldo: 78.5, motivo: '⛔ Sin LOPDP vigente' },
  { cliente: 'BONILLA DÍAZ ROSA', cedula: '0917556677', whatsapp: '+593 99 XXX 8899', saldo: 112.0, motivo: '⛔ Sin LOPDP vigente' },
  { cliente: 'MOREIRA ÁLAVA JUAN', cedula: '0903112233', whatsapp: '+593 99 XXX 4455', saldo: 234.6, motivo: '⛔ Sin LOPDP vigente' },
  { cliente: 'CHOEZ VERA LUIS', cedula: '0905667788', whatsapp: '+593 99 XXX 3344', saldo: 89.75, motivo: '🔁 Dedup 48h (Preventiva nacional hace 14h)' },
  { cliente: 'PÁRRAGA SOLÓRZANO ANA', cedula: '0914778899', whatsapp: '+593 99 XXX 6677', saldo: 56.2, motivo: '🔁 Dedup 48h (esta campaña hace 32h)' },
  { cliente: 'MUÑOZ ARTEAGA DIEGO', cedula: '0911334455', whatsapp: '+593 99 XXX 7788', saldo: 178.9, motivo: '👤 En gestión humana (Ana Vera 2d)' },
  { cliente: 'VELIZ CEDEÑO MARTA', cedula: '0902445566', whatsapp: '+593 99 XXX 1133', saldo: 95.4, motivo: '👤 En gestión humana (Luis Mora)' },
  { cliente: 'INTRIAGO MACÍAS CARLOS', cedula: '0916998877', whatsapp: '+593 99 XXX 0000', saldo: 67.3, motivo: '📵 Número inválido' },
]

function Resumen({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
      <div className="mt-0.5 text-sm text-navy-700">{v}</div>
    </div>
  )
}

function StatCard({ label, value, sub, color }: { label: string; value: string; sub: string; color: 'navy' | 'emerald' | 'amber' | 'blue' }) {
  const colors: Record<typeof color, string> = {
    navy: 'text-navy-700',
    emerald: 'text-emerald-600',
    amber: 'text-amber-600',
    blue: 'text-blue-600',
  }
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={cn('font-mono text-2xl font-semibold mt-0.5', colors[color])}>{value}</div>
      <div className="text-[11px] text-slate-500">{sub}</div>
    </div>
  )
}
